import maplibregl from "maplibre-gl"

import Hook from "../../utils/hook"
import {
  isTouch,
  mapCardDrag,
  mapCardDragActions,
} from "../../utils/map-card-drag"

const SOURCE_ID = `guides-source`
const LAYER_ID = `guides-layer`
const HOVERED_STATE = `guidesHoveredFeatureId`

// the guides page: its map (in the map view) draws the guides as markers;
// `mapData` lists the guides to show. Hovering a guide's card highlights it on
// the map and vice versa. On touchscreens a tapped guide's card shows above
// the page (`mapCard`), dragged down it's dismissed (see
// `utils/map-card-drag.js`)
export const GuidesPage = Hook(`GuidesPage`, {
  ...mapCardDrag(`--guides-page-map-card`),
  values: {
    mapData: Array,
    cardHighlightedClass: String,
    mapCardDraggingClass: String,
    mapCardDraggedAwayClass: String,
  },
  refs: [`maplibre`],
  targets: [`card`, `mapCard`],
  actions: [
    { name: `cardEnter`, targets: `card`, type: `mouseenter` },
    { name: `cardLeave`, targets: `card`, type: `mouseleave` },
    { name: `cardEnter`, targets: `card`, type: `focusin` },
    { name: `cardLeave`, targets: `card`, type: `focusout` },
    ...mapCardDragActions(`mapCard`),
  ],
  mapLoaded: false,
  hoveredFeatureId: null,
  highlightedCard: null,
  mouseMoveHandler: null,
  mouseLeaveHandler: null,
  clickHandler: null,
  popup: null,
  mapCardGuideId: null,

  mounted() {
    this.layerIds = new Set()
    this.sourceIds = new Set()
    this.guidesById = new Map()
    this.guideIdsByCardId = new Map()
    this.indexGuides()
  },

  destroyed() {
    this.clear()
  },

  maplibreRefMounted(maplibreRef) {
    this.mapLoaded = Boolean(maplibreRef.map?.isStyleLoaded?.())

    if (this.mapLoaded) {
      this.draw()
    } else {
      maplibreRef.addLoadHandler(() => {
        this.mapLoaded = true
        this.draw()
      })
    }
  },

  // the map is about to be removed (e.g. back to the grid view)
  maplibreRefDestroyed() {
    this.popup?.remove()
    this.mapLoaded = false
    this.reset()
  },

  // whether the map has loaded once; `isStyleLoaded()` isn't used for this as
  // it's false again whenever a source is loading (e.g. tiles while panning)
  isMapLibreReady() {
    return Boolean(this.maplibreRef?.map && this.mapLoaded)
  },

  mapDataValueChanged() {
    this.update()
  },

  cardEnterAction(e, node) {
    const guideId = this.guideIdsByCardId.get(node.id)
    if (guideId != null) this.setMapHovered(guideId)
  },

  cardLeaveAction() {
    this.setMapHovered(this.hoveredFeatureId ?? -1)
  },

  cardTargetDestroyed(card) {
    if (card === this.highlightedCard) this.highlightedCard = null
  },

  // a new card (another guide's card patches the shown one in place)
  mapCardTargetMounted() {
    this.mapCardDragReset()
  },

  mapCardHide() {
    this.pushEvent(`hide_guide_card`, { id: this.mapCardGuideId })
  },

  setMapHovered(guideId) {
    if (this.isMapLibreReady())
      this.maplibreRef.map.setGlobalStateProperty(HOVERED_STATE, guideId)
  },

  highlightCard(cardId) {
    const card = cardId && this.cardTargets?.find(({ id }) => id === cardId)
    if (card === this.highlightedCard) return

    if (this.highlightedCard)
      this.classRemove(this.cardHighlightedClassValue, this.highlightedCard)
    if (card) this.classAdd(this.cardHighlightedClassValue, card)
    this.highlightedCard = card || null
  },

  draw() {
    this.indexGuides()
    if (!this.isMapLibreReady()) return
    this.clear()
    this.addSourcesAndLayers()
    this.addMapHandlers()
  },

  // shows the current guides without redrawing the layer
  update() {
    if (!this.isMapLibreReady() || !this.sourceIds.has(SOURCE_ID)) {
      this.draw()
      return
    }

    this.indexGuides()
    this.maplibreRef.map.getSource(SOURCE_ID)?.setData(this.pointsData())
  },

  guides() {
    return this.mapDataValue || []
  },

  indexGuides() {
    const guides = this.guides()
    this.guidesById = new Map(guides.map((guide) => [guide.id, guide]))
    this.guideIdsByCardId = new Map(
      guides.map(({ id, card_id: cardId }) => [cardId, id])
    )
  },

  pointsData() {
    const features = this.guides()
      .filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng))
      .map(({ id, title, lat, lng, path }) => ({
        type: `Feature`,
        id,
        geometry: {
          type: `Point`,
          coordinates: [lng, lat],
        },
        properties: { id, title, path },
      }))

    return { type: `FeatureCollection`, features }
  },

  addSourcesAndLayers() {
    if (!this.maplibreRef) return

    const map = this.maplibreRef.map
    const hovered = [`==`, [`id`], [`global-state`, HOVERED_STATE]]

    map.addSource(SOURCE_ID, {
      type: `geojson`,
      data: this.pointsData(),
    })
    this.sourceIds.add(SOURCE_ID)

    map.addLayer({
      id: LAYER_ID,
      source: SOURCE_ID,
      type: `symbol`,
      layout: {
        "icon-image": [`case`, hovered, `none-active`, `none`],
        "icon-size": 0.5,
        "icon-allow-overlap": true,
        "icon-anchor": `bottom`,
        "symbol-z-order": `source`,
        "symbol-sort-key": [`case`, hovered, 1, 0],
      },
    })
    this.layerIds.add(LAYER_ID)
  },

  addMapHandlers() {
    if (!this.maplibreRef) return

    const map = this.maplibreRef.map

    // a tap shows the guide's card (the server renders it, as its card in the
    // grid may not be loaded yet), a mouse click opens the guide
    this.clickHandler = (event) => {
      const guide = this.guidesById.get(event.features?.[0]?.id)
      if (!guide) return

      if (isTouch(event.originalEvent)) {
        this.popup?.remove()
        this.popup = null
        this.mapCardGuideId = guide.id
        this.pushEvent(`show_guide_card`, { id: guide.id })
      } else if (guide.path) {
        window.location.href = guide.path
      }
    }

    this.mouseMoveHandler = (event) => {
      const feature = event.features?.[0]
      const featureId = feature?.id
      if (featureId == null || featureId === this.hoveredFeatureId) return

      const guide = this.guidesById.get(featureId)
      if (!guide) return

      this.hoveredFeatureId = featureId
      this.setMapHovered(featureId)
      this.highlightCard(guide.card_id)
      map.getCanvas().style.cursor = `pointer`

      this.popup?.remove()
      this.popup = new maplibregl.Popup({
        offset: 30,
        anchor: `bottom`,
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat(feature.geometry.coordinates)
        .setText(guide.title)
        .addTo(map)
    }

    this.mouseLeaveHandler = () => {
      this.hoveredFeatureId = null
      this.setMapHovered(-1)
      this.highlightCard(null)
      map.getCanvas().style.cursor = ``
      this.popup?.remove()
      this.popup = null
    }

    map.on(`mousemove`, LAYER_ID, this.mouseMoveHandler)
    map.on(`mouseleave`, LAYER_ID, this.mouseLeaveHandler)
    map.on(`click`, LAYER_ID, this.clickHandler)
  },

  clear() {
    let map = null
    if (this.maplibreRef) map = this.maplibreRef.map

    if (map) {
      map.getCanvas().style.cursor = ``

      if (this.mouseMoveHandler)
        map.off(`mousemove`, LAYER_ID, this.mouseMoveHandler)

      if (this.mouseLeaveHandler)
        map.off(`mouseleave`, LAYER_ID, this.mouseLeaveHandler)

      if (this.clickHandler) map.off(`click`, LAYER_ID, this.clickHandler)
    }

    if (this.isMapLibreReady()) {
      map.setGlobalStateProperty(HOVERED_STATE, -1)

      Array.from(this.layerIds).forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id)
      })

      Array.from(this.sourceIds).forEach((id) => {
        if (map.getSource(id)) map.removeSource(id)
      })
    }

    this.popup?.remove()
    this.reset()
  },

  reset() {
    this.layerIds.clear()
    this.sourceIds.clear()
    this.highlightCard(null)
    this.popup = null
    this.hoveredFeatureId = null
    this.mouseMoveHandler = null
    this.mouseLeaveHandler = null
    this.clickHandler = null
  },
})
